import React from 'react';
import { Typography, Row, Col, Button } from 'antd';
import { tokens } from '../theme/tokens';
import { CreativityType } from '../types/banner';
import { MenuSections } from '../types/menu-sections';

// Hooks
import { useFormState } from '../hooks/banners/creation/form/use-form-state';
import { useFormRenderer } from '../hooks/banners/creation/form/use-form-renderer';
import { useTemplateInfo } from '../hooks/banners/use-template-utils';

// Componentes
import { FormFieldRenderer } from '../components/creativity/form-field-renderer';
import { AccordionFormRenderer } from '../components/creativity/render/accordion-form-renderer';
import { BrandButton } from '../components/UI/buttons';
import { DemoPreview } from '../components/creativity/demo-preview';
import { useResponsiveSizes } from '../hooks/use-responsive-sizes';

const { Title, Text } = Typography;

interface CreativityFormPageProps {
  creativityType: CreativityType;
  onNavigate: (section: MenuSections) => void;
}

export const CreativityFormPage: React.FC<CreativityFormPageProps> = ({ creativityType, onNavigate }) => {
  const { fontSize, spacing, screens } = useResponsiveSizes();
  const {
    formConfig,
    formData,
    errors,
    isCreating,
    isFormComplete,
    fields,
    initializeCreativityType,
    handleFieldChange,
    handleCreateBanner,
    setFieldError
  } = useFormState(creativityType, onNavigate);
  const { rendererType } = useFormRenderer(creativityType, fields.length);
  const { title } = useTemplateInfo(creativityType);

  const FIELD_WIDTH = React.useMemo(() => {
    if (screens.xs) return 400; // móviles pequeños
    if (screens.sm) return 480; // tablets pequeñas
    if (screens.md) return 520; // tablets/laptops pequeñas
    if (screens.lg) return 540; // laptops grandes
    if (screens.xl) return 560; // desktops pequeñas
    return 580; // desktop
  }, [screens]);

  React.useEffect(() => {
    initializeCreativityType();
  }, [initializeCreativityType]);

  if (!formConfig) {
    return (
      <div style={{ maxWidth: '100%', margin: `0 ${spacing.lg}px` }}>
        <Text>Creatividad no encontrada: {creativityType}</Text>
      </div>
    );
  }

  const renderForm = () => {
    const commonProps = {
      fields,
      formData,
      onFieldChange: handleFieldChange,
      fieldWidth: FIELD_WIDTH,
      errors,
      creativityType
    };

    switch (rendererType) {
      case 'accordion':
        return <AccordionFormRenderer {...commonProps} />;
      default:
        return (
          <div
            style={{
              marginTop: spacing.lg,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {fields.map(field => (
              <FormFieldRenderer
                key={field.key}
                field={field}
                formData={formData}
                onFieldChange={handleFieldChange}
                fieldWidth={FIELD_WIDTH}
                errors={errors}
                creativityType={creativityType}
                setFieldError={setFieldError}
              />
            ))}
          </div>
        );
    }
  };

  return (
    <div style={{ maxWidth: '100%', margin: `0 ${spacing.xxl}px`, marginBottom: spacing.lg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }}>
        <Title style={{ fontSize: fontSize.xxxl }}>Nueva creatividad</Title>
        <Title style={{ color: '#68717F', fontSize: fontSize.xl }}>- {title}</Title>
      </div>
      <Text type='secondary' style={{ fontSize: fontSize.xl, marginBottom: spacing.lg, display: 'flex' }}>
        Personaliza tu diseño
      </Text>

      <Row
        gutter={screens.lg ? [24, 24] : [0, 40]}
        style={{
          border: '1px solid #f0f0f0',
          backgroundColor: 'white',
          padding: screens.xs ? spacing.sm : spacing.md
        }}
      >
        <Col
          xs={24}
          lg={14}
          style={{
            paddingRight: screens.md ? spacing.lg : 0,
            borderRight: screens.md ? '1px solid #f0f0f0' : 'none',
            borderBottom: 'none'
          }}
        >
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: FIELD_WIDTH,
                padding: spacing.sm,
                borderRadius: 6,
                backgroundColor: '#F9FAFB'
              }}
            >
              <div style={{ display: 'flex', marginBottom: spacing.xs }}>
                <Text strong style={{ fontSize: fontSize.lg, color: '#0e1a26' }}>
                  Template seleccionado
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }}>
                <Text
                  type='secondary'
                  style={{
                    fontSize: fontSize.lg,
                    display: 'block',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}
                >
                  {title}
                </Text>
                <Button
                  type='link'
                  size={screens.xs ? 'small' : 'middle'}
                  style={{
                    color: tokens.colors.brands.blue[500],
                    padding: 0,
                    fontWeight: 600,
                    fontSize: fontSize.base
                  }}
                  onClick={() => onNavigate('create-asset')}
                >
                  Cambiar
                </Button>
              </div>
            </div>

            {renderForm()}

            <div style={{ width: '100%', maxWidth: FIELD_WIDTH, marginTop: spacing.sm }}>
              <BrandButton onClick={handleCreateBanner} loading={isCreating} disabled={isCreating || !isFormComplete}>
                {isCreating ? 'Procesando...' : 'Crear'}
              </BrandButton>
            </div>
          </div>
        </Col>

        {/* Demo Preview */}
        <Col xs={24} lg={10}>
          <DemoPreview creativityType={creativityType} formData={formData} />
        </Col>
      </Row>
    </div>
  );
};
